/**
 * Epic Analytics List Panel Component
 * 
 * Scrollable list panel displaying epic details with navigation
 * capabilities, occupying 1/3 of the screen width.
 * 
 * Requirements:
 * - 5.1: Display scrollable list of all analyzed epics
 * - 5.2: Show epic key, title, completion date, and story points
 * - 5.4: Sort epics by completion date in descending order by default
 * - 5.5: Occupy approximately one-third of screen width
 */

import React, { useMemo } from 'react'
import { EpicAnalyticsListPanelProps } from '../../../../types/epic-analytics-types'
import { getEpicDetailsUrl } from '../../../../utils/epic-analytics-utils'

/**
 * Epic Analytics List Panel - Side panel with epic details list
 * Implements requirements 5.1, 5.2, 5.4, 5.5
 */
export const EpicAnalyticsListPanel: React.FC<EpicAnalyticsListPanelProps> = ({
  epics,
  loading = false,
  onEpicClick
}) => {
  // Sort epics by completion date in descending order (Requirement 5.4)
  const sortedEpics = useMemo(() => {
    return [...epics].sort((a, b) => {
      const dateA = new Date(a.completionDate).getTime()
      const dateB = new Date(b.completionDate).getTime()
      return dateB - dateA // Descending order (newest first)
    })
  }, [epics])

  // Loading state with skeleton loaders
  if (loading) {
    return (
      <div className="epic-analytics-list-panel w-full h-full flex-shrink-0">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-4">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="flex justify-between items-start mb-2">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="flex justify-between">
                  <div className="h-3 bg-gray-200 rounded w-12"></div>
                  <div className="h-3 bg-gray-200 rounded w-10"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="epic-analytics-list-panel w-full h-full flex-shrink-0">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900">
            Completed Epics ({sortedEpics.length})
          </h3>
          {sortedEpics.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Sorted by completion date (newest first)
            </p>
          )}
        </div>

        {/* Scrollable content area (Requirement 5.1) */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {sortedEpics.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="mb-2">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-1">No epics found</h4>
              <p className="text-sm text-gray-500">
                No completed epics found for the selected time period.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {sortedEpics.map((epic) => (
                <div
                  key={epic.epicKey}
                  onClick={(e) => {
                    // Requirement 5.3: Open epic details in new tab
                    e.preventDefault()
                    const url = getEpicDetailsUrl(epic.epicKey)
                    window.open(url, '_blank', 'noopener,noreferrer')
                    onEpicClick(epic.epicKey)
                  }}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-150 focus:outline-none focus:bg-gray-50"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      const url = getEpicDetailsUrl(epic.epicKey)
                      window.open(url, '_blank', 'noopener,noreferrer')
                      onEpicClick(epic.epicKey)
                    }
                  }}
                  aria-label={`View details for epic ${epic.epicKey}: ${epic.title}. Opens in new tab.`}
                >
                  {/* Epic key and completion date (Requirement 5.2) */}
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors">
                      {epic.epicKey}
                    </h4>
                    <span className="text-xs text-gray-500 font-medium">
                      {new Date(epic.completionDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>

                  {/* Epic title (Requirement 5.2) */}
                  <p className="text-sm text-gray-900 mb-3 line-clamp-2 leading-relaxed">
                    {epic.title}
                  </p>

                  {/* Story points and bug count (Requirement 5.2) */}
                  <div className="flex justify-between items-center text-xs">
                    <div className="flex items-center space-x-4">
                      <span className="text-gray-600 font-medium">
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
                        {epic.storyPoints} {epic.storyPoints === 1 ? 'point' : 'points'}
                      </span>
                      {epic.bugCount > 0 && (
                        <span className="text-red-600 font-medium">
                          <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                          {epic.bugCount} {epic.bugCount === 1 ? 'bug' : 'bugs'}
                        </span>
                      )}
                    </div>
                    <div className="text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EpicAnalyticsListPanel